import { datadogRum } from '@datadog/browser-rum';
import Cookies from 'js-cookie';
import { call, delay, race, type SagaGenerator } from 'typed-redux-saga';
import userflow from 'userflow.js';
import {
  AsyncActionState,
  asyncActionStateMatchers,
  invoke,
  setupSlice,
} from '@owl-frontend/redux';
import { type User, UserRole } from '../../api/interface';
import sessionApiClient, {
  SESSION_TIMEOUT_LIMIT,
} from '../../api/session/client';
import type { FeatureKeys } from './interface';

if (RUM_APPLICATION_ID && RUM_CLIENT_TOKEN) {
  datadogRum.init({
    env: ENV,
    version: __version,
    applicationId: RUM_APPLICATION_ID,
    clientToken: RUM_CLIENT_TOKEN,
    site: 'datadoghq.com',
    service: 'platform',
    sessionSampleRate: 100,
    sessionReplaySampleRate: 100, // if not included, the default is 100
    trackResources: true,
    trackLongTasks: true,
    trackUserInteractions: true,
    defaultPrivacyLevel: 'mask',
  });

  datadogRum.setGlobalContextProperty('viewport', {
    width: window.screen.width,
    height: window.screen.height,
  });

  datadogRum.startSessionReplayRecording();
}

if (USERFLOW_KEY) {
  userflow.init(USERFLOW_KEY);
}

// data = f"{email}{full_name}{client_user_id}{roles}"
const constructPayloadChecksum = async (data: {
  email: string;
  full_name: string;
  client_user_id: string;
  roles: string[];
}): Promise<string> => {
  const { email, full_name, client_user_id, roles } = data;
  const encoder = new TextEncoder();
  const encoded = encoder.encode(
    `${email}${full_name}${client_user_id}${roles.join('')}`
  );
  const hashBuffer = await crypto.subtle.digest('SHA-256', encoded);
  return Array.from(new Uint8Array(hashBuffer))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
};

function* current(): SagaGenerator<User | undefined> {
  const { sessionCurrent, sessionTimeout } = yield* race({
    sessionCurrent: call(sessionApiClient.current),
    sessionTimeout: delay(SESSION_TIMEOUT_LIMIT),
  });

  if (sessionTimeout) {
    throw new Error('TIMED_OUT');
  }

  if (RUM_APPLICATION_ID && RUM_CLIENT_TOKEN && sessionCurrent) {
    datadogRum.setUser({
      id: sessionCurrent.email,
      name: sessionCurrent.email,
      email: sessionCurrent.email,
    });
  }

  if (crypto.subtle) {
    const checksum = Cookies.get('checksum');

    if (!checksum) {
      throw new Error('Missing checksum!');
    }

    const email = Cookies.get('email');

    if (!email) {
      throw new Error('Unable to verify response');
    }

    if (sessionCurrent) {
      const { full_name, client_user_id, roles } = sessionCurrent;

      const payloadChecksum = yield* call(constructPayloadChecksum, {
        email,
        full_name,
        client_user_id,
        roles,
      });

      if (payloadChecksum !== checksum) {
        throw new Error(
          `Checksums do not match! ${payloadChecksum}, ${checksum}`
        );
      }
    }
  }

  return sessionCurrent;
}

const POLL_INTERVAL_MS = 60_000; // 1 minute
function* pollRefreshToken() {
  yield* delay(POLL_INTERVAL_MS);
  while (true) {
    yield* call(sessionApiClient.requestNewToken, {
      authCode: null,
      state: null,
    });
    yield delay(POLL_INTERVAL_MS);
  }
}

function* requestLogout() {
  datadogRum.stopSessionReplayRecording();
  if (USERFLOW_KEY) {
    userflow.reset();
  }

  return yield* call(sessionApiClient.requestLogout);
}

export interface SessionState {
  current?: User;
  features?: {
    [k in FeatureKeys]?: true;
  };
  actions: {
    current: AsyncActionState;
    updateLastActive: AsyncActionState;
    pollRefreshToken: AsyncActionState;
    requestLogin: AsyncActionState<{ url: string; message: string }>;
    requestToken: AsyncActionState;
    requestLogout: AsyncActionState;
    features: AsyncActionState;
  };
}

const initialState: SessionState = {
  actions: {
    current: { status: 'uninitialized' },
    updateLastActive: { status: 'uninitialized' },
    pollRefreshToken: { status: 'uninitialized' },
    requestLogin: { status: 'uninitialized' },
    requestToken: { status: 'uninitialized' },
    requestLogout: { status: 'uninitialized' },
    features: { status: 'uninitialized' },
  },
};

const slice = setupSlice('session', initialState)
  .addAsyncSagas({
    current,
    updateLastActive: invoke(sessionApiClient.updateLastActive),
    requestLogin: invoke(sessionApiClient.requestLogin),
    requestToken: invoke(sessionApiClient.requestNewToken),
    requestLogout,
    features: invoke(sessionApiClient.features),
  })
  .addAsyncSagas({ pollRefreshToken }, { timeoutLimit: -1 })
  .addReducers({
    'current/fulfilled': (state, action) => {
      const user = action.payload;
      if (USERFLOW_KEY && user) {
        userflow.identify(user.client_user_id, {
          name: user.full_name,
          email: user.email,
          role: user.roles.join(', '),
          signed_up_at: user.created_at,
        });
      }
      state.current = {
        ...user,
        has_manager_role:
          user?.roles?.includes(UserRole.Manager) ||
          user?.roles?.includes(UserRole.Manager.toLowerCase()),
      } as User;
    },
    'requestLogin/fulfilled': (_, action) => {
      window.location.replace(action.payload.url);
    },
    'requestLogout/fulfilled': (_, action) => {
      sessionStorage.removeItem('filters');
      sessionStorage.removeItem('pagination');
      window.location.replace(action.payload.url);
    },
    'features/fulfilled': (state, action) => {
      state.features = action.payload.reduce<{ [k in FeatureKeys]?: true }>(
        (acc, curr) => ({
          ...acc,
          [curr]: true,
        }),
        {}
      );
    },
  });

export const actions = slice.actions;
export default slice.addExtra(asyncActionStateMatchers(actions).all());
